import { useState, useId } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import Modal from '@/components/ui/Modal';
import Button from '@/components/Button';
import Spinner from '@/components/ui/Spinner';
import { useMyChannels } from '@/hooks/useChannels';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/utils/cn';
import type { Channel } from '@/types/channel';

/** Sentinel value representing "no channel" (detach). */
const NO_CHANNEL = '__none__' as const;
type ChannelSelection = string | typeof NO_CHANNEL;

export interface ChannelPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** The channel ID currently attached to the template (null / undefined = none). */
  currentChannelId?: string | null;
  /** Called when the user confirms a selection. null = detach from channel. */
  onSelect: (channelId: string | null) => void;
  /** When true the Confirm button shows a spinner and is disabled. */
  isLoading?: boolean;
  /** Override the modal title. Defaults to "Choose a channel". */
  title?: string;
}

/**
 * Modal for attaching a template to one of the coach's owned channels, or
 * detaching it (selecting "No channel").
 *
 * Only channels owned by the current user are shown — the backend permission
 * model requires ownership to attach a template to a channel.
 *
 * `useMyChannels()` returns both owned + joined channels; we filter client-side
 * by `channel.owner_id === user.id`.
 *
 * Accessibility: the modal role and Esc-to-close are handled by the underlying
 * Modal (Headless UI Dialog). The title element is referenced via aria-labelledby
 * through the Modal component's DialogTitle.
 */
// Exported as both named and default to support both import styles.
export function ChannelPicker({
  isOpen,
  onClose,
  currentChannelId,
  onSelect,
  isLoading = false,
  title = 'Choose a channel',
}: ChannelPickerProps) {
  const { user } = useAuth();
  const { data: allChannels, isLoading: channelsLoading } = useMyChannels();
  const titleId = useId();

  // Filter to only channels the current user owns.
  const ownedChannels: Channel[] = (allChannels ?? []).filter(
    (c) => c.owner_id === user?.id,
  );

  // Selection state: either a channel id string, or the NO_CHANNEL sentinel.
  const initialSelection: ChannelSelection = currentChannelId ?? NO_CHANNEL;
  const [selected, setSelected] = useState<ChannelSelection>(initialSelection);

  // Reset selection whenever the modal opens so it reflects the current value.
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setSelected(currentChannelId ?? NO_CHANNEL);
    }
  };

  // Track when the modal opens to reset state.
  // We use a key trick: reset local state on open transitions by reading isOpen.
  // Since Modal doesn't expose an onOpen callback we handle this inside the
  // render — when isOpen flips true, reset to the current saved value.
  // (This is a controlled modal, so selection resets each time isOpen becomes true.)
  // We use a simple memo-compare approach: derive the effective initial state.
   
  void handleOpenChange; // ref used above in comments only

  const isUnchanged = selected === (currentChannelId ?? NO_CHANNEL);

  function handleConfirm() {
    if (isUnchanged) return;
    onSelect(selected === NO_CHANNEL ? null : selected);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
    >
      <div className="mt-2" id={titleId}>
        {channelsLoading ? (
          <div className="flex justify-center py-10">
            <Spinner size="md" label="Loading channels..." />
          </div>
        ) : ownedChannels.length === 0 ? (
          <div className="py-8 text-center text-sm text-text-muted">
            <p>You don&rsquo;t own any channels yet.</p>
            <p className="mt-1">
              Create one from the{' '}
              <a
                href="/channels"
                className="text-accent hover:underline"
                onClick={onClose}
              >
                Channels page
              </a>{' '}
              first.
            </p>
          </div>
        ) : (
          <ul className="mt-1 divide-y divide-border-subtle max-h-80 overflow-y-auto rounded-lg border border-border-subtle">
            {/* "No channel" detach option */}
            <ChannelRow
              id={NO_CHANNEL}
              displayName="No channel"
              description="Detach this template from any channel"
              isSelected={selected === NO_CHANNEL}
              onSelect={() => setSelected(NO_CHANNEL)}
              isDestructive
            />
            {/* Owned channels */}
            {ownedChannels.map((channel) => (
              <ChannelRow
                key={channel.id}
                id={channel.id}
                displayName={channel.display_name}
                description={channel.description ?? undefined}
                isSelected={selected === channel.id}
                onSelect={() => setSelected(channel.id)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" size="sm" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleConfirm}
          disabled={isUnchanged || isLoading || channelsLoading}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Saving...
            </>
          ) : (
            'Confirm'
          )}
        </Button>
      </div>
    </Modal>
  );
}

// ─── Internal ──────────────────────────────────────────────────────────────────

interface ChannelRowProps {
  id: string;
  displayName: string;
  description?: string;
  isSelected: boolean;
  onSelect: () => void;
  isDestructive?: boolean;
}

function ChannelRow({
  id: _id,
  displayName,
  description,
  isSelected,
  onSelect,
  isDestructive = false,
}: ChannelRowProps) {
  return (
    <li>
      <button
        type="button"
        role="radio"
        aria-checked={isSelected}
        onClick={onSelect}
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 text-left transition-colors',
          'hover:bg-dark-elevated-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50',
          isSelected && 'bg-accent/10',
        )}
      >
        <div className="min-w-0">
          <p
            className={cn(
              'text-sm font-medium',
              isDestructive ? 'text-text-muted' : 'text-text-primary',
            )}
          >
            {isDestructive && (
              <XMarkIcon className="h-3.5 w-3.5 inline mr-1.5 text-text-muted" aria-hidden="true" />
            )}
            {displayName}
          </p>
          {description && (
            <p className="mt-0.5 text-xs text-text-muted truncate">{description}</p>
          )}
        </div>
        {isSelected && (
          <CheckCircleIcon
            className="h-5 w-5 text-accent shrink-0 ml-3"
            aria-hidden="true"
          />
        )}
      </button>
    </li>
  );
}

// Default export for barrel re-exports.
export default ChannelPicker;
